import { useState, useEffect } from "react";
import axios from "axios";
import { apiUrl } from "../../../utils/config";
import Footer from "../components/Footer";
import {
  Briefcase, MapPin, Clock, DollarSign, ChevronDown, ChevronUp,
  Send, CheckCircle, AlertCircle, Search,
} from "lucide-react";

const API = apiUrl;

const JOB_TYPE_COLORS = {
  "Full-time":  "bg-green-100 text-green-700",
  "Part-time":  "bg-blue-100 text-blue-700",
  "Contract":   "bg-purple-100 text-purple-700",
  "Internship": "bg-yellow-100 text-yellow-700",
  "Remote":     "bg-teal-100 text-teal-700",
};

const INITIAL_FORM = {
  applicant_name: "", applicant_email: "", applicant_phone: "",
  cover_letter: "", resume_url: "",
};

const inputCls = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 transition";

// ── Apply Drawer ──────────────────────────────────────────────
const ApplyDrawer = ({ job, onClose }) => {
  const [form,       setForm]       = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result,     setResult]     = useState(null); // null | "success" | "error"

  const h = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.applicant_name || !form.applicant_email || !form.cover_letter) return;
    setSubmitting(true);
    setResult(null);
    try {
      await axios.post(`${API}/careers/${job.id}/apply`, form);
      setResult("success");
      setForm(INITIAL_FORM);
    } catch (_) {
      setResult("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white z-50 shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-white">
          <p className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-1">Applying for</p>
          <h2 className="text-xl font-extrabold text-gray-900">{job.title}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{job.department} · {job.location || "Location TBD"}</p>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {result === "success" && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex gap-3 items-start">
              <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-green-800 text-sm">Application submitted!</p>
                <p className="text-xs text-green-600 mt-0.5">We'll review your application and get back to you at <strong>{form.applicant_email || "your email"}</strong>.</p>
              </div>
            </div>
          )}
          {result === "error" && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex gap-3 items-start">
              <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">Something went wrong. Please try again.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-500 mb-1 block">Full Name *</label>
              <input name="applicant_name" value={form.applicant_name} onChange={h} placeholder="Your name" required className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 mb-1 block">Email *</label>
              <input name="applicant_email" value={form.applicant_email} onChange={h} type="email" placeholder="email@example.com" required className={inputCls} />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Phone</label>
            <input name="applicant_phone" value={form.applicant_phone} onChange={h} type="tel" placeholder="+880 1700 000000" className={inputCls} />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Resume / CV Link</label>
            <input name="resume_url" value={form.resume_url} onChange={h} type="url"
              placeholder="https://drive.google.com/your-cv" className={inputCls} />
            <p className="text-xs text-gray-400 mt-1">Google Drive, Dropbox, or any public link</p>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Cover Letter *</label>
            <textarea name="cover_letter" value={form.cover_letter} onChange={h} rows={6} required
              placeholder="Tell us why you're a great fit for this role…"
              className={`${inputCls} resize-none`} />
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50 transition">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !form.applicant_name || !form.applicant_email || !form.cover_letter}
            className="flex-1 py-3 bg-gradient-to-r from-[#f97316] to-red-500 text-white font-extrabold rounded-xl hover:shadow-lg hover:shadow-orange-200 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {submitting ? "Submitting…" : <><Send size={15} /> Apply Now</>}
          </button>
        </div>
      </div>
    </>
  );
};

// ── Job Card ──────────────────────────────────────────────────
const JobCard = ({ job, onApply }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          {/* Icon */}
          <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Briefcase size={22} className="text-orange-500" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="font-extrabold text-gray-900 text-lg leading-tight">{job.title}</h3>
              <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${JOB_TYPE_COLORS[job.job_type] || "bg-gray-100 text-gray-600"}`}>
                {job.job_type}
              </span>
            </div>

            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><Briefcase size={11} />{job.department}</span>
              {job.location && <span className="flex items-center gap-1"><MapPin size={11} />{job.location}</span>}
              {job.salary_range && <span className="flex items-center gap-1"><DollarSign size={11} />{job.salary_range}</span>}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0 self-start sm:self-center">
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 px-4 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50 transition"
            >
              Details {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            <button
              onClick={() => onApply(job)}
              className="px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold shadow-md shadow-orange-200 transition active:scale-95"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="mt-5 pt-5 border-t border-gray-100 space-y-4">
            {job.description && (
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">About the Role</p>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{job.description}</p>
              </div>
            )}
            {job.requirements && (
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Requirements</p>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{job.requirements}</p>
              </div>
            )}
            <button
              onClick={() => onApply(job)}
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#f97316] to-red-500 text-white text-sm font-extrabold shadow-md shadow-orange-200 hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <Send size={15} /> Apply for this Position
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Careers Page ─────────────────────────────────────────
export const CareersPage = () => {
  const [jobs,    setJobs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);
  const [search,  setSearch]  = useState("");
  const [dept,    setDept]    = useState("All");
  const [applyJob, setApplyJob] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await axios.get(`${API}/careers`, { params: { status: "Open" } });
        setJobs(res.data.careers || res.data.list || []);
      } catch (_) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Unique departments for filter
  const departments = ["All", ...Array.from(new Set(jobs.map((j) => j.department).filter(Boolean)))];

  const filtered = jobs.filter((j) => {
    const matchSearch = !search || j.title.toLowerCase().includes(search.toLowerCase()) ||
      (j.department || "").toLowerCase().includes(search.toLowerCase());
    const matchDept = dept === "All" || j.department === dept;
    return matchSearch && matchDept;
  });

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#111] to-gray-800 text-white py-20 px-4 text-center">
        <p className="text-sm font-bold text-[#f97316] uppercase tracking-wider mb-3">We're Hiring</p>
        <h1 className="font-serif text-5xl sm:text-6xl mb-4">
          Join Our <span className="text-[#f97316]">Team</span>
        </h1>
        <p className="text-gray-400 text-lg max-w-xl mx-auto">
          Be part of something delicious. We're looking for passionate people to grow with us.
        </p>
      </section>

      {/* Why Join Us */}
      <section className="bg-orange-50 py-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
          {[
            { icon: "🍽️", title: "Great Culture",    desc: "A team that feels like family, with room to grow." },
            { icon: "📈", title: "Career Growth",    desc: "Regular reviews, training, and promotion paths." },
            { icon: "🎁", title: "Perks & Benefits", desc: "Meals, health support, flexible hours & more." },
          ].map((p) => (
            <div key={p.title} className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
              <div className="text-4xl mb-3">{p.icon}</div>
              <h3 className="font-extrabold text-gray-900 mb-1">{p.title}</h3>
              <p className="text-sm text-gray-500">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Job Listings */}
      <section className="max-w-4xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Open Positions</h2>
        <p className="text-gray-500 text-sm mb-8">
          {loading ? "Loading…" : `${filtered.length} position${filtered.length !== 1 ? "s" : ""} available`}
        </p>

        {/* Search + Filter */}
        {!loading && !error && jobs.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title or department…"
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 transition" />
            </div>
            <select value={dept} onChange={(e) => setDept(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white transition">
              {departments.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
        )}

        {/* States */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gray-100" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-100 rounded w-1/3" />
                    <div className="h-3 bg-gray-100 rounded w-1/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-16">
            <AlertCircle size={36} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 text-sm">Failed to load jobs. Please try again later.</p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-16">
            <Briefcase size={36} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 text-sm">
              {jobs.length === 0 ? "No open positions right now. Check back soon!" : "No results match your search."}
            </p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="space-y-4">
            {filtered.map((job) => (
              <JobCard key={job.id} job={job} onApply={setApplyJob} />
            ))}
          </div>
        )}
      </section>

      {/* CTA Banner */}
      <section className="bg-gradient-to-r from-[#f97316] to-red-500 py-14 px-4 text-center text-white">
        <h2 className="text-2xl font-extrabold mb-2">Don't see your perfect role?</h2>
        <p className="text-orange-100 text-sm mb-6">Send us your CV anyway — we're always looking for great talent.</p>
        <a href="/contact"
          className="inline-block bg-white text-orange-600 font-extrabold px-8 py-3 rounded-2xl hover:shadow-xl transition-all">
          Get in Touch
        </a>
      </section>

      {/* Apply Drawer */}
      {applyJob && (
        <ApplyDrawer job={applyJob} onClose={() => setApplyJob(null)} />
      )}

      <Footer />
    </div>
  );
};

export default CareersPage;
